import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import {
  App,
  Breadcrumb,
  Button,
  Card,
  Checkbox,
  Col,
  Form,
  Input,
  Modal,
  Popconfirm,
  Row,
  Space,
  Typography
} from "antd";
import { ColumnsType } from 'antd/es/table';
import { Table } from 'components';
import { actions, AddRole, PermissionRow, roleTypeLists, SaveRolePrivilegePermissionsDTO, UpdateRole } from 'features/users/types';
import {
  useAddRole,
  useGetRolePrivilegePermissions,
  useGetRoleTypes,
  useSaveRolePrivilegePermissions,
  useUpdateRole
} from 'features/users/use-cases';
import { isArray } from 'lodash';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ROUTES, SideBarMenus } from 'routes/constants';
import { HasPermission } from 'utils/CheckPermissions';

const { Title } = Typography;

interface Role {
  id: number;
  title: string;
}


export const RoleManagementView = () => {

  const [data, setData] = useState<PermissionRow[]>([]);
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  const [selectedRoleId, setSelectedRoleId] = useState<number>(0);
  const [selectedRoleName, setSelectedRoleName] = useState<string | null>(null);
  const { mutate: saveRolePrivilegePermissions } = useSaveRolePrivilegePermissions();
  const { data: roleList } = useGetRoleTypes();
  const [roles, setRoles] = useState<roleTypeLists[]>([]);
  const { mutate: saveRole } = useAddRole();
  const { mutate: updateRole } = useUpdateRole();
  const { data: rolePrivilegePermissions, refetch } = useGetRolePrivilegePermissions(selectedRoleId, {
    cacheTime: 0,
  });
  const { message } = App.useApp();
  useEffect(() => {
    if (roleList != undefined && isArray(roleList)) {
      setRoles(roleList);
    }
    if (rolePrivilegePermissions != undefined) {
      setData(rolePrivilegePermissions);

      const defaultExpanded = rolePrivilegePermissions
        .filter((row) => rolePrivilegePermissions.some((r) => r.parentKey === row.key)).map((row) => row.key);

      setExpandedKeys(defaultExpanded);
    }

  }, [roleList, rolePrivilegePermissions])
  const getChildren = (parentKey: string) => data.filter((item) => item.parentKey === parentKey);
  const revokeAllPermissions = () => {
    if (rolePrivilegePermissions != undefined) {
      const updated = rolePrivilegePermissions.map(row => ({
        ...row,
        permissions: Object.fromEntries(
          Object.keys(row.permissions).map(key => [key, false])
        )
      }));
      setData(updated);
    }
  };
  const handleCheckboxChange = (recordKey: string, actionKey: string, checked: boolean) => {
    setData((prev) =>
      prev.map((row) => {
        if (row.key === recordKey) {
          return {
            ...row,
            permissions: { ...row.permissions, [actionKey]: checked },
          };
        }
        if (row.parentKey === recordKey) {
          return {
            ...row,
            permissions: { ...row.permissions, [actionKey]: checked },
          };
        }

        return row;
      })
    );
  };
  const handleSelectAllColumn = (actionKey: string, checked: boolean) => {
    setData((prev) =>
      prev.map((row) => ({
        ...row,
        permissions: { ...row.permissions, [actionKey]: checked },
      }))
    );
  };
  const handleSubmit = () => {
    if (selectedRoleId == 0) {
      message.error('Please select any role for save permissions.');

      return false;
    }
    const formatted = data.map((row) => ({
      key: row.key,
      permissions: row.permissions,
    }));
    const saveURolePayload: SaveRolePrivilegePermissionsDTO = {
      roleId: selectedRoleId,
      rolePermission: formatted
    };
    saveRolePrivilegePermissions(saveURolePayload);
  };
  const flatRows = data.flatMap((row) => {
    if (!row.parentKey) {
      const children = expandedKeys.includes(row.key) ? getChildren(row.key) : [];

      return [row, ...children];
    }

    return row.parentKey ? [] : [row];
  });
  const columns: ColumnsType<PermissionRow> = [
    {
      title: 'Pages',
      dataIndex: 'title',
      key: 'title',
      render: (_: any, record: PermissionRow) => {
        const hasChildren = data.some((r) => r.parentKey === record.key);
        const isExpanded = expandedKeys.includes(record.key);

        return (
          <div style={{ display: 'flex', alignItems: 'center', paddingLeft: record.parentKey ? 24 : 0 }}>
            {hasChildren && !record.parentKey && (
              <span
                onClick={() =>
                  setExpandedKeys((prev) =>
                    prev.includes(record.key)
                      ? prev.filter((k) => k !== record.key)
                      : [...prev, record.key]
                  )
                }
                style={{ cursor: 'pointer', marginRight: 8 }}
              >
                {isExpanded ? '➖' : '➕'}
              </span>
            )}
            {record.title}
          </div>
        );
      },
    },
    ...actions.map(({ key, label }) => ({
      title: (
        <Checkbox
          onChange={(e) => handleSelectAllColumn(key, e.target.checked)}
          indeterminate={
            data.some((row) => row.permissions[key]) &&
            !data.every((row) => row.permissions[key])
          }
          checked={data.every((row) => row.permissions[key])}
        >
          {label}
        </Checkbox>
      ),
      key,
      dataIndex: key,
      align: 'center' as const,
      render: (_: any, record: PermissionRow) => {
        const children = getChildren(record.key);
        const isParent = children.length > 0;

        let checked = record.permissions[key];
        let indeterminate = false;

        if (isParent) {
          const all = children.every((child) => child.permissions[key]);
          const some = children.some((child) => child.permissions[key]);
          checked = all;
          indeterminate = some && !all;
        }

        return (
          <Checkbox
            checked={checked}
            indeterminate={indeterminate}
            onChange={(e) => handleCheckboxChange(record.key, key, e.target.checked)}
          />
        );
      },
    })),
  ];
  const [search, setSearch] = useState<string>("");
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [form] = Form.useForm();
  const filteredRoles = roles.filter(role =>
    role.title.toLowerCase().includes(search.toLowerCase())
  );
  const handleEdit = (role: Role) => {
    setEditingRole(role);
    form.setFieldsValue({ name: role.title });
    setModalVisible(true);
  };
  const handleDelete = (id: number) => {
    setRoles(prev => prev.filter(role => role.id !== id));
    const updateRoleRequest: UpdateRole = {
      id: id,
      role: "Inactive role",
      type: 2
    };
    updateRole(updateRoleRequest);
  };
  const handleSave = () => {
    form.validateFields().then((values: any) => {
      setRoles(prev =>
        prev.map(role =>
          role.id === editingRole?.id ? { ...role, name: values.name } : role
        )
      );
      if (editingRole == null) {
        const addRoleValue: AddRole = {
          name: values.name
        }
        saveRole(addRoleValue);
      } else {
        const updateRoleRequest: UpdateRole = {
          id: editingRole?.id,
          role: values.name,
          type: 1
        };
        updateRole(updateRoleRequest);
      }
      setModalVisible(false);
      setEditingRole(null);
      form.resetFields();
    });
  };
  const openAddRoleModal = () => {
    setEditingRole(null);
    form.resetFields();
    setModalVisible(true);
  };
  const handleTitleClick = async (role: Role) => {
    setSelectedRoleId(role.id);
    setSelectedRoleName(role.title);

    const result = await refetch();
    if (result.data != undefined)
      setData(result.data);

  };

  return (
    <>
      <Breadcrumb separator=">" style={{ marginBottom: 10 }}>
        <Breadcrumb.Item>
          <Link to={ROUTES.USERS_APP}>User Management</Link>
        </Breadcrumb.Item>
        <Breadcrumb.Item>
          <Link to={ROUTES.USERS_APP}>Admins</Link>
        </Breadcrumb.Item>
        <Breadcrumb.Item>
          Roles
        </Breadcrumb.Item>
      </Breadcrumb>
      <Row gutter={0} style={{ padding: 0 }}>

        <Col span={6}>

          <div style={{
            width: 300,
            padding: 16,
            margin: 0,
            backgroundColor: "#fff",
            borderRadius: 8,
            boxShadow: "0 0 4px rgba(0,0,0,0.1)",
          }}>

            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
              <Title level={5} style={{ margin: 0 }}>Role List</Title>
              <Button
                type="link"
                icon={<PlusOutlined />}
                onClick={openAddRoleModal}
                style={{ padding: 0, color: "black" }}
              >
                Add Role
              </Button>
            </div>
            <Input
              placeholder="Search Role"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ marginBottom: 12 }}
            />

            <Space direction="vertical" style={{ width: "100%" }}>
              {filteredRoles.map(role => (
                <div
                  key={role.id}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "6px 10px",
                    borderBottom: "1px solid #f0f0f0",
                    borderRadius: 4,
                  }}
                >

                  <span
                    style={{
                      cursor: "pointer",
                      fontWeight: selectedRoleId === role.id ? "bold" : "normal", // 💡 bold on select
                      color: selectedRoleId === role.id ? "black" : "#D1712A", // 💡 blue on select
                    }}
                    onClick={() => handleTitleClick(role)}
                  >
                    {role.title}
                  </span>
               
                  <Space size="small">
                     {HasPermission(SideBarMenus.ADMIN, 'edit') ? (
                    <EditOutlined
                      style={{ cursor: "pointer", color: "#1890ff" }}
                      onClick={() => handleEdit(role)}
                    />
                    ):null }

                     {HasPermission(SideBarMenus.ADMIN, 'delete') ? (
                    <Popconfirm
                      title="Delete this role?"
                      onConfirm={() => handleDelete(role.id)}
                    >
                      <DeleteOutlined style={{ cursor: "pointer", color: "#ff4d4f" }} />
                    </Popconfirm>
                      ):null }
                  </Space>
                
                </div>
              ))}
            </Space>

            {/* Modal for Add/Edit Role */}
            <Modal
              title={editingRole ? "Edit Role" : "Add Role"}
              open={modalVisible}
              onOk={handleSave}
              onCancel={() => setModalVisible(false)}
              okText={editingRole ? "Update" : "Save"}
            >
              <Form form={form} layout="vertical">
                <Form.Item
                  name="name"
                  label="Role Name"
                  rules={[{ required: true, message: "Please enter a role name" }]}
                >
                  <Input />
                </Form.Item>
              </Form>
            </Modal>
          </div>
        </Col>

        <Col span={18} style={{ padding: 0, margin: 0 }}>
        
          <Card title={selectedRoleName ? selectedRoleName : ''} className='role_card'
            extra={
              <>
               {HasPermission(SideBarMenus.ADMIN, 'edit') ? (
                <Button type="primary" onClick={handleSubmit} style={{ marginLeft: '8px' }}>
                  Submit
                </Button>
                 ):null }
                
                {HasPermission(SideBarMenus.ADMIN, 'edit') ? (
                <Button type="primary" onClick={revokeAllPermissions} style={{ marginLeft: '8px' }} danger >
                  Reset Permissions
                </Button>
                 ):null }
               

              </>
            }
          >

            <Table
              columns={columns}
              dataSource={flatRows}
              rowKey="key"
              pagination={false}
              bordered
            />

          </Card>

        </Col>
      </Row>
    </>
  );
};
